import { MS_PER_DAY, MS_PER_HOUR } from '../constants.js';

// Medication dosage constants
export const DOSAGE_PARAMS = {
  paracetamol: {
    dosePerKg: 15,        // mg/kg per dose — the top of the usual 10–15 mg/kg range
    maxSingleMg: 1000,    // absolute cap per dose (1 g), whatever the weight
    maxDailyPerKg: 60,    // mg/kg per day
    maxDosesPerDay: 4,
    minIntervalHours: 4,
    maxIntervalHours: 6,
    intervalLabel: '4-6 hours'
  },
  ibuprofen: {
    dosePerKg: 10,        // mg/kg per dose — the top of the usual 5–10 mg/kg range
    maxSingleMg: 400,     // absolute cap per dose, whatever the weight
    maxDailyPerKg: 30,    // mg/kg per day
    maxDosesPerDay: 3,
    minIntervalHours: 6,
    maxIntervalHours: 8,
    intervalLabel: '6-8 hours'
  }
};

// Safety thresholds
export const SAFETY_THRESHOLDS = {
  dailyLimitWarning: 0.9,      // Warn at 90% of daily limit
  cautionIntervalHours: 1,     // Extra caution period after minimum interval
  crossMedIntervalHours: 2,    // Minimum time between different medications
  weightMatchTolerance: 0.1,   // kg tolerance for matching historical records
  // How far a dose may be adjusted above the recommendation before it's refused.
  // dosePerKg is already the top of the published per-dose range, so nothing
  // above it is "safe" as such; this band only absorbs measuring to the
  // syringe's markings (2.1 → 2.2 ml) and is flagged as a high dose.
  highDoseTolerance: 0.10
};

// Utility functions
export const parseConcentration = (concStr) => {
  const [mg, ml] = concStr.split('/').map(parseFloat);
  return { mg, ml };
};

// Down to the 0.1 ml below. The epsilon keeps float noise from costing a step:
// 2.1 computed as 2.0999999999 still floors to 2.1, not 2.0.
export const floorToTenth = (x) => Math.floor(x * 10 + 1e-6) / 10;

export const calculateDosage = (weightKg, mgPerKg, concentration) => {
  const totalMg = weightKg * mgPerKg;
  const { mg, ml } = parseConcentration(concentration);
  return (totalMg / mg) * ml;
};

export const isWithinTimeWindow = (timestamp, windowMs) => {
  return new Date(timestamp) >= new Date(Date.now() - windowMs);
};

// Decides whether a past dose "belongs" to the current calculation for safety
// checks (interval + daily-limit). With a child selected we match exactly by id.
// Without one (manual weight entry), we fall back to matching by weight (±0.1kg)
// — a deliberate best-effort so manual-weight users still get interval warnings.
// Caveat: two children of near-identical weight can then count against each
// other's limits. Selecting a child (or the app-wide Default Child) avoids this.
export const matchesChild = (entry, selectedChildId, weight) => {
  if (selectedChildId) return entry.childId === selectedChildId;
  return Math.abs(entry.weight - weight) < SAFETY_THRESHOLDS.weightMatchTolerance;
};

// Every dose is computed from the profile weight, and young children outgrow it
// quickly — an old weight quietly under-doses. A weight is trusted for a month
// under age 1 (fast growth) and two months after that. Profiles saved before
// weightUpdatedAt existed have no date and count as needing a check.
export const weightFreshness = (child) => {
  if (!child) return null;
  const birth = new Date(child.dob);
  const ageMonths = (Date.now() - birth.getTime()) / (MS_PER_DAY * 30.44);
  const limitDays = ageMonths < 12 ? 30 : 60;
  if (!child.weightUpdatedAt) return { known: false, stale: true, days: null, limitDays };
  const days = Math.max(0, Math.floor((Date.now() - new Date(child.weightUpdatedAt).getTime()) / MS_PER_DAY));
  return { known: true, stale: days >= limitDays, days, limitDays };
};

// Daily-limit warnings depend on the dose being given, so they're computed from
// whatever dose is currently selected (recommended or user-adjusted) rather than
// baked in at calculation time.
export const dailyLimitWarnings = (usedMg, doseMg, maxDaily) => {
  // `daily` marks these for the Calculator's verdict line rather than a box.
  if (usedMg + doseMg > maxDaily) {
    return [{ type: 'danger', daily: true, message: `⚠️ DANGER: Would exceed 24h limit by ${(usedMg + doseMg - maxDaily).toFixed(0)} mg!` }];
  }
  if (usedMg + doseMg > maxDaily * SAFETY_THRESHOLDS.dailyLimitWarning) {
    return [{ type: 'warning', daily: true, message: `Would reach ${((usedMg + doseMg) / maxDaily * 100).toFixed(0)}% of the 24 h limit` }];
  }
  return [];
};

// Per-dose size check on the amount actually being given (it can be adjusted
// with −/+). The recommendation is the per-dose maximum (shown as "Maximum
// dose"), so at or below it: fine. Up to highDoseTolerance above
// it: a caution ("High dose"). Beyond that, or over the drug's absolute cap per
// dose: danger, which blocks registering like the other danger warnings.
// Compared in ml against the recommendation, which calc() rounds down to the
// 0.1 ml below dosePerKg — so the next step up is the first one over it.
// `dose` marks these for the verdict line in the dose box rather than the list.
export const doseSizeWarnings = (med, doseMl, doseMg, recommendedMl, weight) => {
  const params = DOSAGE_PARAMS[med];
  if (doseMg > params.maxSingleMg + 0.5) {
    return [{ type: 'danger', dose: true, level: 'over',
      message: `Over the ${params.maxSingleMg} mg maximum per dose` }];
  }
  if (!(doseMl > parseFloat(recommendedMl) + 1e-9)) return [];
  const limitMg = weight * params.dosePerKg * (1 + SAFETY_THRESHOLDS.highDoseTolerance);
  if (doseMg <= limitMg + 0.5) {
    return [{ type: 'caution', dose: true, level: 'high',
      message: `High dose — over the ${recommendedMl} ml maximum` }];
  }
  return [{ type: 'danger', dose: true, level: 'over',
    message: `Too high — more than 10% over the maximum` }];
};

// Everything the safety checks derive from history for one child + drug: what's
// already on board in the last 24h, and the two time-based gates (this drug's
// own interval, and the cross-medication interval since the other drug).
//
// Deliberately a pure function of `dosageHistory` and called at *render*, not
// frozen into the calculation result. Freezing it meant that a dose logged on
// the other phone — or simply the clock advancing — left the result panel
// showing a stale total, a stale "Remaining", and a stale danger gate, so the
// app could permit a dose it should have blocked. The standing 24h tracker read
// live history, so the two could contradict each other on screen at once.
export const doseSafetyFromHistory = (dosageHistory, med, selectedChildId, weight) => {
  const params = DOSAGE_PARAMS[med];
  const matches = (e, m) => e.medication === m
    && isWithinTimeWindow(e.timestamp, MS_PER_DAY)
    && matchesChild(e, selectedChildId, weight);

  const recentDoses = dosageHistory.filter(e => matches(e, med));
  const usedMg = recentDoses.reduce((sum, e) => sum + parseFloat(e.mgTotal || 0), 0);
  const maxDaily = weight * params.maxDailyPerKg;
  const warnings = [];

  // `interval` marks the timing warnings: they still gate registering, but the
  // Calculator shows them through its single next-dose banner rather than
  // repeating them in the result panel.
  // History is newest-first, so [0] / find() give the most recent match.
  if (recentDoses.length > 0) {
    const hoursSinceLast = (Date.now() - new Date(recentDoses[0].timestamp)) / MS_PER_HOUR;
    if (hoursSinceLast < params.minIntervalHours) {
      warnings.push({ type: 'danger', interval: true, message: `⚠️ TOO SOON: Only ${hoursSinceLast.toFixed(1)}h since last dose. Wait ${params.minIntervalHours}h between doses.` });
    } else if (hoursSinceLast < params.minIntervalHours + SAFETY_THRESHOLDS.cautionIntervalHours) {
      warnings.push({ type: 'caution', interval: true, message: `⏱️ CAUTION: Only ${hoursSinceLast.toFixed(1)}h since last dose.` });
    }
  }

  const otherMed = med === 'paracetamol' ? 'ibuprofen' : 'paracetamol';
  const lastOtherDose = dosageHistory.find(e => matches(e, otherMed));
  if (lastOtherDose) {
    const hoursSinceLast = (Date.now() - new Date(lastOtherDose.timestamp)) / MS_PER_HOUR;
    if (hoursSinceLast < SAFETY_THRESHOLDS.crossMedIntervalHours) {
      warnings.push({ type: 'danger', interval: true, message: `⚠️ CROSS-MED: Only ${hoursSinceLast.toFixed(1)}h since last ${otherMed}. Wait ${SAFETY_THRESHOLDS.crossMedIntervalHours}h between medications.` });
    }
  }

  return {
    recentDoses, usedMg, maxDaily, warnings,
    remainingMg: maxDaily - usedMg,
    lastDoseTime: recentDoses.length > 0 ? recentDoses[0].timestamp : null,
  };
};

// The Calculator's up-front "when can the next dose be given" banner, as a pure
// function of history (so tests.html can check it against the gate above).
//
// Both constraints gate the next dose and both are checked: this drug's own
// minIntervalHours *and* crossMedIntervalHours since the other drug. Whichever
// clears last is the one in force. It must agree with doseSafetyFromHistory's
// danger warnings — a mismatch is what once let the banner say "can be given
// now" while Register refused (see CLAUDE.md). Returns null with no relevant
// history in the last 24h.
export const nextDoseFromHistory = (dosageHistory, medication, selectedChild, w) => {
  // History is newest-first, so find() returns the most recent matching dose.
  const lastDoseOf = (med) => dosageHistory.find(e =>
    e.medication === med &&
    isWithinTimeWindow(e.timestamp, MS_PER_DAY) &&
    matchesChild(e, selectedChild, w)
  );

  const otherMed = medication === 'paracetamol' ? 'ibuprofen' : 'paracetamol';
  const lastSame = lastDoseOf(medication);
  const lastOther = lastDoseOf(otherMed);
  if (!lastSame && !lastOther) return null;

  const constraints = [];
  if (lastSame) {
    const given = new Date(lastSame.timestamp);
    constraints.push({
      med: medication,
      cross: false,
      given,
      time: new Date(given.getTime() + DOSAGE_PARAMS[medication].minIntervalHours * MS_PER_HOUR)
    });
  }
  if (lastOther) {
    const given = new Date(lastOther.timestamp);
    constraints.push({
      med: otherMed,
      cross: true,
      given,
      time: new Date(given.getTime() + SAFETY_THRESHOLDS.crossMedIntervalHours * MS_PER_HOUR)
    });
  }

  const blocking = constraints.reduce((a, b) => (b.time > a.time ? b : a));
  // Mirrors the CAUTION warning in doseSafetyFromHistory: allowed, but within
  // an hour of the minimum gap since this same medicine.
  const caution = !!lastSame && Date.now() - new Date(lastSame.timestamp)
    < (DOSAGE_PARAMS[medication].minIntervalHours + SAFETY_THRESHOLDS.cautionIntervalHours) * MS_PER_HOUR;
  return { ...blocking, available: blocking.time <= new Date(), caution, lastSame };
};
